import * as React from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'
import { Check, ChevronDown, ChevronUp } from 'lucide-react'
import { cn } from '@/lib/utils'

const Select = SelectPrimitive.Root
const SelectGroup = SelectPrimitive.Group
const SelectValue = SelectPrimitive.Value

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      // Unnati react-select control (Select.tsx classNames + _select.css):
      // 48px (CONTROL_SIZES.md min-h-12), rounded-xl, grey-100 fill with a
      // grey-100 hairline border; focused/open → 1px primary border + 1px
      // primary ring on a transparent fill. Value + placeholder are semibold
      // (placeholder grey-400). `.field` keeps the white-on-bare-page repaint
      // from index.css (skipped when focused/open).
      'field flex h-[var(--control-h)] w-full items-center justify-between gap-2 whitespace-nowrap rounded-xl border border-clay-100 bg-clay-100 py-2 pl-3 pr-3 text-left text-sm font-semibold text-clay-800 transition duration-150',
      'focus:border-primary focus:bg-transparent focus:outline-none focus:ring-1 focus:ring-primary data-[state=open]:border-primary data-[state=open]:bg-transparent data-[state=open]:ring-1 data-[state=open]:ring-primary',
      'data-[placeholder]:text-clay-400 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
      'dark:border-clay-700 dark:bg-clay-700 dark:text-clay-100 dark:focus:bg-transparent dark:data-[state=open]:bg-transparent',
      className,
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-6 w-6 shrink-0 text-clay-500 dark:text-clay-400" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', collisionPadding = 8, ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        'relative z-50 flex flex-col max-h-[min(13.625rem,var(--radix-select-content-available-height))] min-h-[50px] min-w-[8rem] max-w-[calc(100vw-1rem)] overflow-hidden rounded-xl border border-clay-100 bg-white text-popover-foreground shadow-menu ring-1 ring-clay-100 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 dark:border-clay-700 dark:bg-clay-900 dark:ring-clay-700',
        // Unnati .select-menu my-2 → 8px gap from the control.
        position === 'popper' &&
          'data-[side=bottom]:translate-y-2 data-[side=left]:-translate-x-2 data-[side=right]:translate-x-2 data-[side=top]:-translate-y-2',
        className,
      )}
      position={position}
      collisionPadding={collisionPadding}
      {...props}
    >
      {/* 5 options visible (see dropdownListMaxHeightClass), then the
          viewport scrolls with a visible scrollbar (.select-viewport). The
          Radix scroll arrows are left out: they took a row's height. */}
      <SelectPrimitive.Viewport
        className={cn(
          'select-viewport px-2 py-2',
          position === 'popper' &&
            'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label ref={ref} className={cn('px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-clay-500', className)} {...props} />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      // Unnati DefaultOptionWithFocus (Select.tsx) + .select-option: 14px
      // semibold, py-2.5, rounded-md, label inset 16px (px-2 + ml-2).
      // Hover / keyboard focus = grey-100 fill (grey-700 dark) — NOT a tint.
      // Selected = primary-subtle fill + primary text + 20px check on the
      // right, and it keeps that look while hovered (focus styling only
      // applies to unselected rows).
      'relative flex w-full cursor-pointer select-none items-center rounded-md py-2.5 pl-4 pr-9 text-sm font-semibold text-clay-800 outline-none transition-colors dark:text-clay-100',
      'focus:bg-clay-100 focus:text-clay-800 dark:focus:bg-clay-700 dark:focus:text-clay-100',
      'data-[state=checked]:bg-primary-subtle data-[state=checked]:text-primary data-[state=checked]:focus:bg-primary-subtle data-[state=checked]:focus:text-primary dark:data-[state=checked]:text-primary dark:data-[state=checked]:focus:bg-primary-subtle dark:data-[state=checked]:focus:text-primary',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    {...props}
  >
    <span className="absolute right-2 flex h-5 w-5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-5 w-5" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator ref={ref} className={cn('-mx-1 my-1 h-px bg-clay-200 dark:bg-clay-700', className)} {...props} />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
}
